import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { adminApi } from '../../lib/http';
import {
  PortalButton,
  PortalCard,
  PortalEmpty,
  PortalInput,
  PortalModal,
  PortalSelect,
  PortalStatus,
} from '../../components/portal/PortalElements';

const emptyForm = {
  name: '',
  username: '',
  password: '',
  confirmPassword: '',
  mobile: '',
  email: '',
  designation: '',
  status: 'active',
};

export default function AdminStaffManagement() {
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [resetPassword, setResetPassword] = useState({ newPassword: '', confirmPassword: '' });

  async function loadStaff() {
    const response = await adminApi.get('/admin/staff', {
      params: search ? { search } : undefined,
    });
    setStaff(response.data || []);
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadStaff().catch((error) => toast.error(error.message || 'Unable to load staff.'));
    }, 250);

    return () => window.clearTimeout(timer);
  }, [search]);

  const stats = useMemo(
    () => ({
      total: staff.length,
      active: staff.filter((item) => item.status === 'active').length,
      inactive: staff.filter((item) => item.status === 'inactive').length,
    }),
    [staff],
  );

  async function createStaff() {
    try {
      await adminApi.post('/admin/staff', form);
      toast.success('Staff account created.');
      setCreateOpen(false);
      setForm(emptyForm);
      await loadStaff();
    } catch (error) {
      toast.error(error.message || 'Unable to create staff.');
    }
  }

  async function updateStaff() {
    if (!selectedStaff) {
      return;
    }

    try {
      await adminApi.patch(`/admin/staff/${selectedStaff._id}`, {
        name: form.name,
        username: form.username,
        mobile: form.mobile,
        email: form.email,
        designation: form.designation,
        status: form.status,
      });
      toast.success('Staff account updated.');
      setSelectedStaff(null);
      await loadStaff();
    } catch (error) {
      toast.error(error.message || 'Unable to update staff.');
    }
  }

  async function toggleStatus(member) {
    try {
      await adminApi.patch(`/admin/staff/${member._id}/status`, {
        status: member.status === 'active' ? 'inactive' : 'active',
      });
      toast.success('Staff status updated.');
      await loadStaff();
    } catch (error) {
      toast.error(error.message || 'Unable to update staff status.');
    }
  }

  async function resetStaffPassword() {
    if (!selectedStaff) {
      return;
    }

    try {
      await adminApi.patch(`/admin/staff/${selectedStaff._id}/reset-password`, resetPassword);
      toast.success('Password reset.');
      setResetPassword({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.message || 'Unable to reset password.');
    }
  }

  async function deleteStaff(member) {
    try {
      await adminApi.delete(`/admin/staff/${member._id}`);
      toast.success('Staff account archived.');
      setSelectedStaff(null);
      await loadStaff();
    } catch (error) {
      toast.error(error.message || 'Unable to remove staff.');
    }
  }

  function editStaff(member) {
    setSelectedStaff(member);
    setForm({
      name: member.name || '',
      username: member.username || '',
      password: '',
      confirmPassword: '',
      mobile: member.mobile || '',
      email: member.email || '',
      designation: member.designation || '',
      status: member.status || 'active',
    });
  }

  return (
    <div className="portal-stack">
      <section className="portal-grid portal-grid-stats">
        <PortalCard className="portal-stat"><div className="portal-stat-label">Total Staff</div><div className="portal-stat-value">{stats.total}</div></PortalCard>
        <PortalCard className="portal-stat"><div className="portal-stat-label">Active</div><div className="portal-stat-value">{stats.active}</div></PortalCard>
        <PortalCard className="portal-stat"><div className="portal-stat-label">Inactive</div><div className="portal-stat-value">{stats.inactive}</div></PortalCard>
      </section>

      <PortalCard>
        <div className="portal-filters">
          <PortalInput
            label="Search staff"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name, username, email"
          />
          <PortalButton onClick={() => { setForm(emptyForm); setCreateOpen(true); }}>
            Add staff
          </PortalButton>
        </div>
      </PortalCard>

      <PortalCard>
        {staff.length ? (
          <div className="portal-table-wrap">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Mobile</th>
                  <th>Designation</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((member) => (
                  <tr key={member._id}>
                    <td>{member.name}</td>
                    <td>{member.username}</td>
                    <td>{member.mobile || '-'}</td>
                    <td>{member.designation || '-'}</td>
                    <td>
                      <PortalStatus tone={member.status === 'active' ? 'success' : 'danger'}>
                        {member.status}
                      </PortalStatus>
                    </td>
                    <td>
                      <div className="portal-action-row">
                        <PortalButton variant="secondary" onClick={() => editStaff(member)}>
                          Edit
                        </PortalButton>
                        <PortalButton variant="secondary" onClick={() => toggleStatus(member)}>
                          {member.status === 'active' ? 'Disable' : 'Enable'}
                        </PortalButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <PortalEmpty title="No staff found" body="Create the first staff account to enable the staff portal." />
        )}
      </PortalCard>

      <PortalModal open={createOpen} title="Create staff account" onClose={() => setCreateOpen(false)}>
        <div className="portal-grid portal-grid-2">
          <PortalInput label="Name" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
          <PortalInput label="Username" value={form.username} onChange={(event) => setForm((current) => ({ ...current, username: event.target.value }))} />
          <PortalInput label="Password" type="password" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} />
          <PortalInput label="Confirm password" type="password" value={form.confirmPassword} onChange={(event) => setForm((current) => ({ ...current, confirmPassword: event.target.value }))} />
          <PortalInput label="Mobile" value={form.mobile} onChange={(event) => setForm((current) => ({ ...current, mobile: event.target.value }))} />
          <PortalInput label="Email" type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} />
          <PortalInput label="Designation" value={form.designation} onChange={(event) => setForm((current) => ({ ...current, designation: event.target.value }))} />
          <PortalSelect label="Status" value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </PortalSelect>
        </div>
        <div className="portal-action-row">
          <PortalButton onClick={createStaff}>Create</PortalButton>
        </div>
      </PortalModal>

      <PortalModal
        open={Boolean(selectedStaff)}
        title={selectedStaff ? `Manage ${selectedStaff.name}` : 'Manage staff'}
        onClose={() => setSelectedStaff(null)}
      >
        {selectedStaff ? (
          <div className="portal-stack">
            <div className="portal-grid portal-grid-2">
              <PortalInput label="Name" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
              <PortalInput label="Username" value={form.username} onChange={(event) => setForm((current) => ({ ...current, username: event.target.value }))} />
              <PortalInput label="Mobile" value={form.mobile} onChange={(event) => setForm((current) => ({ ...current, mobile: event.target.value }))} />
              <PortalInput label="Email" type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} />
              <PortalInput label="Designation" value={form.designation} onChange={(event) => setForm((current) => ({ ...current, designation: event.target.value }))} />
              <PortalSelect label="Status" value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </PortalSelect>
            </div>
            <div className="portal-action-row">
              <PortalButton onClick={updateStaff}>Save</PortalButton>
              <PortalButton variant="secondary" onClick={() => deleteStaff(selectedStaff)}>
                Archive
              </PortalButton>
            </div>
            <div className="portal-divider" />
            <div className="portal-grid portal-grid-2">
              <PortalInput label="New password" type="password" value={resetPassword.newPassword} onChange={(event) => setResetPassword((current) => ({ ...current, newPassword: event.target.value }))} />
              <PortalInput label="Confirm password" type="password" value={resetPassword.confirmPassword} onChange={(event) => setResetPassword((current) => ({ ...current, confirmPassword: event.target.value }))} />
            </div>
            <PortalButton variant="secondary" onClick={resetStaffPassword}>
              Reset password
            </PortalButton>
          </div>
        ) : null}
      </PortalModal>
    </div>
  );
}
